import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api, mensagemDoErro } from '../api';
import { useSessao } from '../sessao';
import { AvisoDoPortal, nomeDaTabela, type AcessoQueSai } from '../Lixeira';
import { Aviso, Campo, Carregando, SegurarParaConfirmar, Vazio } from '../ui';

/**
 * A exclusão definitiva na homologação — onda 235, `RN-214` (0211).
 *
 * «Por enquanto como está em homologação preciso de super poderes de
 *  exclusão de amostras, OS, relatórios de recebimento, equipamentos,
 *  grupos, etc. E quero que exclua a trilha de registro no banco de dados
 *  também. Crie essa opção exclusivamente para o meu perfil Admin.»
 *                                            — Humberto, 02/10/2026
 *
 * Três passos, e o do meio é o que importa: (1) achar o registro; (2) ver,
 * tabela por tabela, tudo o que vai sair com ele — a simulação roda a
 * exclusão de verdade e desfaz; (3) digitar o código e segurar o botão.
 *
 * A tela só aparece na engrenagem de quem tem a marca, na instalação que
 * liga a porta (`EXCLUSAO_DEFINITIVA=sim`). Para qualquer outro, a rota
 * responde 404 e esta tela diz que a porta não existe aqui.
 */

interface Tipo { chave: string; nome: string }
interface Linha { id: string; rotulo: string; detalhe: string | null }
interface Resumo {
  alvo: Linha;
  simulacao: boolean;
  total: number;
  trilha: number;
  apagadas: Record<string, number>;
  desligadas: Record<string, number>;
  /** 285 (`RN-230`): os acessos ao portal que saem com o cliente. */
  portal?: AcessoQueSai[];
}

const nomeDa = nomeDaTabela;
const num = (n: number) => Number(n).toLocaleString('pt-BR');

export function ExclusaoDefinitiva() {
  const { eu } = useSessao();
  const [busca] = useSearchParams();
  const [tipos, setTipos] = useState<Tipo[] | null>(null);
  const [indisponivel, setIndisponivel] = useState<string | null>(null);
  const [tipo, setTipo] = useState(busca.get('tipo') ?? 'os');
  const [q, setQ] = useState('');
  const [achados, setAchados] = useState<Linha[]>([]);
  const [procurando, setProcurando] = useState(false);
  const [escolhido, setEscolhido] = useState<string | null>(busca.get('id'));
  const [resumo, setResumo] = useState<Resumo | null>(null);
  const [simulando, setSimulando] = useState(false);
  const [confirmacao, setConfirmacao] = useState('');
  const [apagando, setApagando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [feito, setFeito] = useState<Resumo | null>(null);

  useEffect(() => {
    api<Tipo[]>('/exclusao-definitiva/tipos')
      .then(setTipos)
      .catch((e) => setIndisponivel(mensagemDoErro(e)));
  }, []);

  // busca enquanto digita, com folga
  useEffect(() => {
    if (q.trim().length < 2) { setAchados([]); return; }
    let vivo = true;
    setProcurando(true);
    const t = window.setTimeout(() => {
      api<Linha[]>(`/exclusao-definitiva/buscar?tipo=${encodeURIComponent(tipo)}`
        + `&q=${encodeURIComponent(q.trim())}`)
        .then((r) => { if (vivo) setAchados(r); })
        .catch((e) => { if (vivo) setErro(mensagemDoErro(e)); })
        .finally(() => { if (vivo) setProcurando(false); });
    }, 250);
    return () => { vivo = false; window.clearTimeout(t); };
  }, [q, tipo]);

  // escolheu → simula
  useEffect(() => {
    if (!escolhido) { setResumo(null); return; }
    let vivo = true;
    setSimulando(true);
    setErro(null);
    setResumo(null);
    setConfirmacao('');
    api<Resumo>('/exclusao-definitiva/simular', { corpo: { tipo, id: escolhido } })
      .then((r) => { if (vivo) setResumo(r); })
      .catch((e) => { if (vivo) setErro(mensagemDoErro(e)); })
      .finally(() => { if (vivo) setSimulando(false); });
    return () => { vivo = false; };
  }, [escolhido, tipo]);

  const apagadas = useMemo(
    () => Object.entries(resumo?.apagadas ?? {}).sort((a, b) => b[1] - a[1]),
    [resumo]);
  const desligadas = useMemo(
    () => Object.entries(resumo?.desligadas ?? {}).sort((a, b) => b[1] - a[1]),
    [resumo]);
  const confere = !!resumo
    && confirmacao.trim().toUpperCase() === resumo.alvo.rotulo.trim().toUpperCase();

  const apagar = async () => {
    if (!resumo || !escolhido) return;
    setApagando(true);
    setErro(null);
    try {
      const r = await api<Resumo>('/exclusao-definitiva/executar',
        { corpo: { tipo, id: escolhido, confirmacao } });
      setFeito(r);
      setResumo(null);
      setEscolhido(null);
      setAchados([]);
      setQ('');
    } catch (e) {
      setErro(mensagemDoErro(e));
    } finally { setApagando(false); }
  };

  if (indisponivel || (eu && !eu.exclusaoDefinitiva)) {
    return (
      <div>
        <h1>Exclusão definitiva</h1>
        <Aviso tipo="info" titulo="Esta porta não existe aqui">
          <p>
            A exclusão definitiva só abre na homologação, para a conta que
            tem a marca, e nunca com uma persona vestida.
          </p>
        </Aviso>
      </div>
    );
  }
  if (!tipos) return <Carregando texto="Abrindo a exclusão definitiva" />;

  const nomeDoTipo = tipos.find((t) => t.chave === tipo)?.nome ?? tipo;

  return (
    <div className="exclusao-definitiva">
      <div className="linha-topo">
        <div className="cresce">
          <h1>Exclusão definitiva</h1>
          <p className="sub">
            Só na homologação · apaga o registro, tudo o que depende dele e a
            trilha de auditoria de tudo isso. Não tem desfazer.
          </p>
        </div>
      </div>

      <Aviso tipo="atencao" titulo="Isto não deixa rastro no banco">
        <p>
          Nenhuma das travas do sistema vale aqui: OS emitida, ensaio
          aprovado, laudo assinado, custódia da amostra — sai tudo. A trilha
          desses registros também sai, e a própria exclusão não é anotada
          nela. Fotos e PDFs guardados no armazenamento ficam lá, sem
          ninguém apontando para eles.
        </p>
      </Aviso>

      {feito && (
        <Aviso tipo="ok" titulo={`${feito.alvo.rotulo} foi apagado de vez`}>
          <p>
            {num(feito.total)} linha(s) em {Object.keys(feito.apagadas).length} tabela(s)
            {' '}e {num(feito.trilha)} linha(s) da trilha.
          </p>
          {(feito.portal ?? []).some((a) => a.fica_sem_cliente) && (
            <p>
              Ficaram sem cliente — e sem código — no portal:{' '}
              {(feito.portal ?? []).filter((a) => a.fica_sem_cliente)
                .map((a) => a.email).join(', ')}.
            </p>
          )}
        </Aviso>
      )}

      <section className="cartao">
        <h2>1 · O que apagar</h2>
        <div className="exd-tipos" role="radiogroup" aria-label="Tipo de registro">
          {tipos.map((t) => (
            <button key={t.chave} type="button" role="radio"
              aria-checked={t.chave === tipo}
              className={t.chave === tipo ? 'chip ativo' : 'chip'}
              onClick={() => { setTipo(t.chave); setEscolhido(null); setAchados([]); }}>
              {t.nome}
            </button>
          ))}
        </div>
        <Campo rotulo={`Procurar ${nomeDoTipo.toLowerCase()}`}
          dica="Código, número ou nome — pelo menos dois caracteres"
          value={q} onChange={(e) => setQ(e.target.value)} autoComplete="off" />
        {procurando && <p className="sub">Procurando…</p>}
        {!procurando && q.trim().length >= 2 && achados.length === 0 && (
          <Vazio>Nada encontrado.</Vazio>
        )}
        {achados.length > 0 && (
          <ul className="exd-achados">
            {achados.map((a) => (
              <li key={a.id}>
                <button type="button"
                  className={a.id === escolhido ? 'exd-achado ativo' : 'exd-achado'}
                  onClick={() => setEscolhido(a.id)}>
                  <b>{a.rotulo}</b>
                  {a.detalhe && <span>{a.detalhe}</span>}
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {erro && <Aviso tipo="erro" titulo={erro} />}
      {simulando && <Carregando texto="Simulando a exclusão (nada é apagado)" />}

      {resumo && (
        <section className="cartao">
          <h2>2 · O que vai sair com {resumo.alvo.rotulo}</h2>
          {resumo.alvo.detalhe && <p className="sub">{resumo.alvo.detalhe}</p>}
          <p>
            <b>{num(resumo.total)}</b> linha(s) em <b>{apagadas.length}</b> tabela(s),
            e <b>{num(resumo.trilha)}</b> linha(s) da trilha de auditoria.
          </p>
          <AvisoDoPortal portal={resumo.portal} apagadas={resumo.apagadas} />
          <table className="exd-tabela">
            <thead><tr><th>Apagado</th><th className="num">Linhas</th></tr></thead>
            <tbody>
              {apagadas.map(([t, n]) => (
                <tr key={t}><td>{nomeDa(t)} <code>{t}</code></td>
                  <td className="num">{num(n)}</td></tr>
              ))}
              <tr><td>Trilha de auditoria <code>auditoria</code></td>
                <td className="num">{num(resumo.trilha)}</td></tr>
            </tbody>
          </table>
          {desligadas.length > 0 && (
            <>
              <p className="sub">
                Ficam, mas deixam de apontar para o que foi apagado (a coluna
                vira vazia):
              </p>
              <table className="exd-tabela">
                <thead><tr><th>Desligado</th><th className="num">Linhas</th></tr></thead>
                <tbody>
                  {desligadas.map(([t, n]) => (
                    <tr key={t}><td><code>{t}</code></td><td className="num">{num(n)}</td></tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </section>
      )}

      {resumo && (
        <section className="cartao">
          <h2>3 · Confirmar</h2>
          <Campo rotulo={`Digite «${resumo.alvo.rotulo}» para confirmar`}
            value={confirmacao} onChange={(e) => setConfirmacao(e.target.value)}
            autoComplete="off" />
          <SegurarParaConfirmar
            texto={apagando ? 'Apagando…' : `Apagar ${resumo.alvo.rotulo} de vez`}
            desabilitado={!confere || apagando}
            aoConfirmar={() => { void apagar(); }} />
          <p className="sub">
            <Link to="/">Voltar ao início</Link>
          </p>
        </section>
      )}
    </div>
  );
}

/**
 * O atalho nas fichas (OS, amostra, recebimento, equipamento): leva à
 * tela já com o registro escolhido e a simulação rodando. Não desenha
 * nada para quem não tem a porta.
 */
export function ExcluirDeVez({ tipo, id }: { tipo: string; id: string }) {
  const { eu } = useSessao();
  if (!eu?.exclusaoDefinitiva || !id) return null;
  return (
    <Link className="botao perigo" title="Exclusão definitiva (homologação)"
      to={`/exclusao-definitiva?tipo=${tipo}&id=${encodeURIComponent(id)}`}>
      Excluir de vez
    </Link>
  );
}
